// ** MUI Components
import {
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardHeader,
  MenuItem,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { useState,useEffect } from "react";

// ** Custom Component Import
import CustomTextField from "../../components/mui/text-field";
import AdvancedFilter from "./component/AdvancedFilter";

// ** Layout Import
import UserLayout from "../../components/layouts/UserLayout";

// ** Dialog Imports
import CreateRequestDialog from "./component/CreateRequest";
import TagManagementDialog from "../Customers/component/TagManagement";
import ServiceRequestDialog from "./component/ServiceRequestform";

// ** Icon Imports
import Icon from "../../components/icon";
import AddIcon from "@mui/icons-material/Add";

// ** Logic Imports
import { useIndexLogic } from "./logic/IndexLogic";
import { useServiceRequestPermission } from "./permissions/RequestPermissions";

const Requests = () => {  
  //#region Permissions
    const { ServiceRequestPermissionProps } = useServiceRequestPermission();
    const {
      hasConvertToPermission,
      hasStatusUpdatePermission,
      hasRequestSearchPermission,
      hasServiceSavePermission,
    } = ServiceRequestPermissionProps;
  //#endregion Permissions

  const {IndexProps} = useIndexLogic();  

  const {
    columns,     
    contentHeightFixed,
    handleOpenNewRequestDlg,
    handleCloseNewRequestDlg,
    handleOpenTagManagementDlg,
    handleCloseTagManagementDlg,
    handleClearSelection,
    handleGridSelection,
    handleSearch,
    handleClearSearch,
    loading,
    setLoading,
    filteredRows, 
    fetchRequests,
    searchText, 
    data,
    paginationText,
    paginationModel, 
    setPaginationModel,
    openNewRequestDlg, 
    selectedRows, 
    rowSelectionModel, 
    setRowSelectionModel,
    openTagManagementDlg, 
    tagManagementData,
    handleAddTag,
    handleDeleteTag,
    Tag,
    setTag,
    serviceRequest,
  } = IndexProps;
    

  const apiRef = useGridApiRef();
   useEffect(() => {
      if (!apiRef.current) return;
  
      apiRef.current.autosizeColumns({
        includeHeaders: true,
        includeOutliers: true,
        expand: false,
      });
    }, [ filteredRows]);
   
  return (
    <UserLayout contentHeightFixed={contentHeightFixed} activeLink={"Requests"}>
      <Card>
        <CardHeader
          title={
            <Box
              sx={{
                mb: { xs: 0, md: 0 },
                width: "100%",
              }}
            >
              <Box
                sx={{
                  border: "1px solid #D9D9D9",
                  borderRadius: 1,
                  px: { xs: 2, md: 4 },
                  py: { xs: 1.5, md: 2 },
                  maxWidth: "max-content",
                  width: { xs: "100%", md: "auto" },
                }}
              >
                <Typography variant="body2" sx={{ pointerEvents: "none" }}>
                  {filteredRows.length === (data || []).length
                    ? `Displaying ${paginationText} records`
                    : `Displaying ${paginationText} records`}
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Box
              sx={{
                display: "flex",
                gap: { xs: 2, md: 3 },
                flexWrap: "wrap",
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <CustomTextField
                value={searchText}
                placeholder="Search"
                disabled={!hasRequestSearchPermission}
                {...(hasRequestSearchPermission && {
                  onChange: (e) => handleSearch(e.target.value),
                })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="tabler:search" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {searchText && (
                        <IconButton
                          edge="end"
                          onClick={handleClearSearch}
                          aria-label="clear"
                          sx={{ mr: 2 }}
                        >
                          <Icon icon="tabler:x" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <AdvancedFilter
                hasSearchPermission={hasRequestSearchPermission}
              />
              <Button
                onClick={handleOpenNewRequestDlg}
                color="main"
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!hasServiceSavePermission}
              >
                New Request
              </Button>
            </Box>
          }
          sx={{
            py: { xs: 3, md: 4 },
            flexDirection: { xs: "column", md: "row" },
            "& .MuiCardHeader-action": {
              m: 0,
            },
            alignItems: { xs: "flex-start", md: "center" },
            gap: { xs: 2, md: 0 },
          }}
        />
        {selectedRows.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              gap: 4,
              alignItems: "center",
              mx: 6,
              mt: 2,
              mb: 4,
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                border: "1px solid #E7E7E7",
                borderRadius: 1,
                pr: 4,
              }}
            >
              <IconButton sx={{ mx: 1 }} onClick={handleClearSelection}>
                <Icon icon="tabler:x" />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  cursor: "default",
                  userSelect: "none",
                }}
              >
                {selectedRows.length}{" "}
                {selectedRows.length === 1 ? "Record" : "Records"} Selected
              </Typography>
            </Box>
            <CustomTextField
              select
              defaultValue="0"
              disabled={!hasConvertToPermission}
            >
              <MenuItem value="0" disabled>
                Convert To
              </MenuItem>
              <MenuItem value="assessment">Assessment</MenuItem>
              <MenuItem value="estimate">Estimate</MenuItem>
              <MenuItem value="workOrder">Work Order</MenuItem>
              <MenuItem value="invoice">Invoice</MenuItem>
            </CustomTextField>
            <CustomTextField
              select
              defaultValue="0"
              disabled={!hasStatusUpdatePermission}
            >
              <MenuItem value="0" disabled>
                Status
              </MenuItem>
              <MenuItem value="underReview">Under Review</MenuItem>
              <MenuItem value="onHold">On Hold</MenuItem>
              <MenuItem value="Canceled">Canceled</MenuItem>
            </CustomTextField>
            <Button variant="contained" color="main">
              Apply Changes
            </Button>
            <Divider orientation="vertical" flexItem />
            <Box
              sx={{
                border: "1px solid #D9D9D9",
                borderRadius: 1,
                display: "flex",
                gap: 2,
                px: 2,
              }}
            >
              <Tooltip title="Tag Management">
                <IconButton onClick={handleOpenTagManagementDlg}>
                  <Icon icon="tabler:tag" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Selected">
                <IconButton color="error">
                  <Icon icon="tabler:trash" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
        <Box sx={{ height: "75vh" }}>
          <DataGridPro
            // pagination
             apiRef={apiRef}
            rows={filteredRows}
            rowHeight={62}
            columns={columns}
            checkboxSelection
            // pageSizeOptions={[
            //   10,
            //   50,
            //   { value: filteredRows.length, label: "Show All" },
            // ]}
            disableRowSelectionOnClick
            // paginationModel={paginationModel}
            // onPaginationModelChange={setPaginationModel}
            selectionModel={rowSelectionModel}
            onSelectionModelChange={(newSelectionModel) => {
              handleGridSelection(newSelectionModel);
              setRowSelectionModel(newSelectionModel);
            }}
            slots={{
              footer: () => null
            }}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  completeWorkBy: false,
                },
              },
            }}
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "none",
              },
              // *****************************************
              //  ** Do not Remove below commented styles **
              // *****************************************

              // "& .MuiDataGrid-columnHeader": {
              //   backgroundColor: "#F4F8F9"
              // },
              // "& .MuiDataGrid-virtualScrollerRenderZone": {
              //   backgroundColor: '#F4F8F9'
              // },
              // "& .MuiDataGrid-row": {
              //   mb: 2,
              //   backgroundColor: '#fff',
              //   borderRadius: 2,
              //   borderTop: 'none !important',
              //   '&:hover': {
              //     backgroundColor: '#fff',
              //     scale: '(1, 1.3)'
              //   }
              // },
              // "& .MuiDataGrid-row.Mui-selected": {
              //   backgroundColor: '#fff',
              //   boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              // },
              // "& .MuiDataGrid-cell": {
              //   borderTop: 'none'
              // }
            }}
          />
        </Box>
      </Card>

      {/* Pop up dialogs */}

      {/* New Request Dialog */}
      <CreateRequestDialog
        open={openNewRequestDlg}
        handleClose={handleCloseNewRequestDlg}
        setLoading={setLoading}
      />

      {/* Tag Management Dialog */}
      <TagManagementDialog
        open={openTagManagementDlg}
        handleClose={handleCloseTagManagementDlg}
        data={{ title: "Tag Management", entity: "request" }}
        handleAddTag={handleAddTag}
        handleDeleteTag={handleDeleteTag}
        tags={serviceRequest?.tags}
        tag={Tag}
        setTag={setTag}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </UserLayout>
  );
};

export default Requests;